// "Mentioned in" — who points at this record. Entirely derived from @mentions in
// rich fields and entry bodies, so it costs nothing to maintain and there is no
// form to fill in.
//
// The v2 division of labor, stated once: if a relationship needs a date or a
// number it is a typed edge (a showing, a support edge); otherwise you just
// mention it while writing. This panel is the payoff for the second half.

import { useNavigate } from 'react-router-dom'

import { accentVar, KINDS, titleField } from '../lib/model'
import { backlinks } from '../lib/reducer'
import { useStore } from '../lib/store'
import { Icon, iconName } from './Icon'

export function Backlinks({ id }: { id: string }) {
  const { state, version } = useStore()
  const navigate = useNavigate()
  void version

  // Collapse several mentions from the same record into one chip, keeping the
  // most specific "via" so the label still says how it points here.
  const seen = new Map<string, string>()
  for (const link of backlinks(state, id)) {
    if (!seen.has(link.from)) seen.set(link.from, link.via)
  }

  if (seen.size === 0) {
    return (
      <p className="text-body py-2" style={{ color: 'var(--faint)' }}>
        Nothing points here yet. @mention it in a note or in another record's description and it
        turns up here.
      </p>
    )
  }

  return (
    <div className="flex flex-wrap gap-2 py-1">
      {[...seen.entries()].map(([from, via]) => {
        const rec = state.entities[from]
        if (!rec) return null
        const def = KINDS[rec.kind]
        return (
          <button
            key={from}
            type="button"
            onClick={() => navigate(`/e/${from}`)}
            className="text-meta inline-flex items-center gap-2 rounded-full border px-3 py-1"
            style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
          >
            <Icon
              name={iconName(def.icon)}
              size={12}
              style={{ color: `var(${accentVar(rec.kind)})` }}
            />
            <span className="max-w-[18ch] truncate">
              {String(rec.fields[titleField(rec.kind)] ?? '') || `Untitled ${def.singular}`}
            </span>
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 'var(--text-micro)',
                color: 'var(--faint)',
              }}
            >
              {via}
            </span>
          </button>
        )
      })}
    </div>
  )
}
